import React, { useContext } from "react";
import { ModalContext } from "components/common";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import { BOOST_DATA_QUERY } from "./query";

const RESUME_BOOST_SUBSCRIPTION = gql`
  mutation ResumeBoostSubscription($email: String!) {
    resumePausedBoostSubscription(email: $email) {
      status
    }
  }
`;

const PauseButton = ({ disabled, handlePause }) => (
  <button
    className={`text-white self-end px-3 py-1 mt-4 ${
      disabled ? "bg-gray" : "bg-red-500 hover:bg-red-600"
    }`}
    type="button"
    onClick={handlePause}
    disabled={disabled}
  >
    Pause Subscription
  </button>
);

const ResumeButton = ({ disabled, loading, handleResume }) => (
  <button
    className="text-white self-end px-3 py-1 mt-4 btn-green"
    type="button"
    onClick={handleResume}
    disabled={disabled}
  >
    {loading ? "Resuming Subscription..." : "Resume Subscription"}
  </button>
);

const Detail = ({ scheduledResumeDate }) => {
  if (scheduledResumeDate) {
    return (
      <div>
        <span>Payment collection paused until</span>
        <span className="font-bold ml-2">{`${scheduledResumeDate.toDateString()}`}</span>
      </div>
    );
  }
  return (
    <div>
      Payments are currently being collected. Pause to stop collecting payments
      for a set period of time.
    </div>
  );
};

/**
 * @param {Object} props
 * @param {string} props.email - (Required) The email associated with the subscription.
 * @param {Date} props.scheduledResumeDate - (Required) The date the subscription will resume.
 * @param {Date} props.scheduledCancellationDate - (Required) The date the subscription will cancel.
 */
const BoostSubscriptionPause = (props) => {
  const {
    email,
    scheduledResumeDate,
    scheduledCancellationDate,
  } = props;
  const isMarkedForCancellation = !!scheduledCancellationDate;
  const isPaused = !!scheduledResumeDate;

  const { openModal } = useContext(ModalContext);

  const [resumeSubscription, { loading, error }] = useMutation(
    RESUME_BOOST_SUBSCRIPTION,
    {
      refetchQueries: [{ query: BOOST_DATA_QUERY, variables: { email } }],
    },
  );

  const errorMessage = error?.graphQLErrors?.[0].message || "";
  const disabled = !!(loading || isMarkedForCancellation);

  const tips = [];
  if (isMarkedForCancellation) {
    tips.push("subscription cannot be paused when it is set to cancel");
  }

  const buttonNote = isPaused
    ? "resume at start of billing cycle"
    : "pause immediately";

  const handleResume = () => {
    resumeSubscription({ variables: { email } });
  };

  const handlePause = (e) => {
    openModal(e, "PauseBoostSubscriptionModal", { email });
  };

  return (
    <div className="flex flex-col w-full">
      <Detail scheduledResumeDate={scheduledResumeDate} />
      {!!tips.length &&
        tips.map((tip) => (
          <p className="mt-2 text-sm italic text-gray-700">{tip}</p>
        ))}
      {isPaused ? (
        <ResumeButton
          disabled={disabled}
          loading={loading}
          handleResume={handleResume}
        />
      ) : (
        <PauseButton disabled={disabled} handlePause={handlePause} />
      )}
      <p className="text-sm self-end text-gray-700">{buttonNote}</p>
      <p className="mt-1 text-red-500">{errorMessage}</p>
    </div>
  );
};

export default BoostSubscriptionPause;
